import React from 'react';
import { Link, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import {
  getAuth,
  signInWithEmailAndPassword,
  signInWithCustomToken,
  createUserWithEmailAndPassword,
  getIdToken,
} from '../../helpers/auth';
import { fetchAPI } from '../../ducks/api';
import { resolveViewMode } from '../../utils/viewMode';
import './index.css';
import PasswordField from './PasswordField';
import PasswordResetForm from './PasswordResetForm';
import firebase from 'firebase/compat/app';
import NubodLogo from '../../assets/logos/nubod-logo-black.png';

/**
 * AuthModal — V2 design.
 *
 * Three modes driven by props/state:
 *   - login (default on `/login`)
 *   - signup (when `isAssessment` is true, used after the assessment flow)
 *   - password reset (toggled via local `passwordReset` state)
 *
 * The auth-logic side of this file is intentionally unchanged from the
 * legacy version — only chrome / inputs / buttons / labels were
 * modernized. Don't touch the Firebase + `loginUser` / `registerUser`
 * call sequences here; the assessment flow downstream depends on them.
 */
function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

class AuthModal extends React.Component {
  state = {
    loginObj: {},
    passwordError: null,
    connectError: null,
    loading: false,
    passwordReset: false,
    // Passwordless "Sign in with a code" (login only) — a sibling of the
    // password form that funnels into the same handleLoginProcess. Does not
    // touch the preserved password/registration sequences.
    codeMode: false,
    codeSent: false,
    code: '',
  };

  componentDidMount() {
    if (this.props.showPasswordReset) {
      this.setState({ passwordReset: true });
    }
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.showPasswordReset && this.props.showPasswordReset) {
      this.setState({ passwordReset: true });
    }
  }

  // --- form state -----------------------------------------------------------

  handleLoginForm = (e, field) => {
    this.setState({
      loginObj: { ...this.state.loginObj, [field]: e.target.value },
    });
  };

  // --- auth handlers (logic preserved verbatim from legacy AuthModal) ------

  // Resolve the right post-login landing for this user.
  //   - Trainers (and higher) in trainer mode → Studio Overview (/dashboard/admin)
  //   - Trainers explicitly flipped to client mode  → /dashboard/home
  //   - Clients → /dashboard/home
  // The login saga passes the user document to the callback as the
  // second arg (see `ducks/Auth/saga.js` line 100), so we read role
  // there without touching the store directly.
  resolveLoginLanding = (loggedInUser) => {
    const user = loggedInUser?.user || loggedInUser || {};
    const role = user.role || 'client';
    const isTrainerPlus =
      role === 'trainer' ||
      role === 'organization_admin' ||
      role === 'platform_admin';
    if (!isTrainerPlus) return '/dashboard/home';
    // "View as client" is gated on the server `canSelfTrack` flag (the
    // AppConfig trainerSelfTracking mode + role). Users who can't self-track
    // always land on the studio overview, ignoring any stale localStorage
    // viewMode from a prior session.
    let stored = 'trainer';
    try {
      stored = localStorage.getItem('viewMode') || 'trainer';
    } catch (_) {
      // ignore
    }
    return resolveViewMode(user, stored) === 'client'
      ? '/dashboard/home'
      : '/dashboard/admin';
  };

  handleLoginProcess = (token, type) => {
    const { loginUser, history } = this.props;
    const messageKey = type === 'password' ? 'passwordError' : 'connectError';
    this.setState({ [messageKey]: null, loading: true });
    loginUser({ token }, async (error, data) => {
      if (!error) {
        this.setState({ [messageKey]: null, loading: false });
        if (!this.props.isAssessment) {
          await sleep(200);
          return history.push(this.resolveLoginLanding(data));
        }
        await sleep(200);
        return history.push('/assessment');
      }
      return this.setState({ [messageKey]: error.message, loading: false });
    });
  };

  handleRegistrationProcess = async (registrationData, type) => {
    const { registerUser, history } = this.props;
    const messageKey = type === 'password' ? 'passwordError' : 'connectError';
    this.setState({ [messageKey]: null, loading: true });
    if (!this.props.isAssessment) {
      await sleep(200);
      return history.push('/dashboard/home');
    }

    let payload;
    if (
      this.props.authRedirectRoute &&
      this.props.authRedirectRoute.length > 0
    ) {
      payload = {
        profileData: { ...this.props.assessmentProfile },
        nutritionResults: { ...this.props.assessmentResults[0] },
        callback: () => this.props.history.push(this.props.authRedirectRoute),
      };
    }

    delete registrationData.password;
    registerUser(
      {
        ...registrationData,
        profile: { ...((payload && payload.profileData) || {}) },
        nutrition: payload && payload.nutritionResults,
      },
      async (error) => {
        if (error) {
          const user = firebase.auth().currentUser;
          user && (await user.delete());
          return this.setState({ [messageKey]: error.message, loading: false });
        }
        this.setState({ [messageKey]: null, loading: false });
        this.props.history.push('/verify-email');
      },
    );
  };

  handleSubmit = async (event) => {
    event.preventDefault();
    const { email, password } = this.state.loginObj;
    this.setState({ loading: true });
    if (this.props.isLogin) {
      try {
        await signInWithEmailAndPassword(email, password);
        const token = await getIdToken();
        this.handleLoginProcess(token, 'password');
      } catch (error) {
        this.setState({ passwordError: error.message, loading: false });
      }
    } else {
      const { name, lastName } = this.state.loginObj;
      if (!name || !lastName) {
        return this.setState({
          passwordError: 'Please fill the required fields',
          loading: false,
        });
      }
      try {
        const result = await createUserWithEmailAndPassword(email, password);
        const { uid } = result.user;
        const token = await getIdToken();
        this.handleRegistrationProcess(
          { ...this.state.loginObj, token, uid },
          'password',
        );
      } catch (error) {
        this.setState({ passwordError: error.message, loading: false });
      }
    }
  };

  // --- passwordless code login (funnels into handleLoginProcess) -----------

  handleSendCode = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const { email } = this.state.loginObj;
    if (!email) {
      return this.setState({ connectError: 'Enter your email first.' });
    }
    this.setState({ connectError: null, loading: true });
    try {
      await fetchAPI('/auth/login-code/request', { method: 'POST' }, { email });
      this.setState({ codeSent: true, loading: false });
    } catch (error) {
      // The server is enumeration-safe (always 200), so this only fires on a
      // network/transport error.
      this.setState({
        connectError:
          "Couldn't send the code. Check your connection and try again.",
        loading: false,
      });
    }
  };

  handleVerifyCode = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const { email } = this.state.loginObj;
    const { code } = this.state;
    if (!code || code.length !== 6) {
      return this.setState({
        connectError: 'Enter the 6-digit code from your email.',
      });
    }
    this.setState({ connectError: null, loading: true });
    try {
      const { customToken } = await fetchAPI(
        '/auth/login-code/verify',
        { method: 'POST' },
        { email, code },
      );
      await signInWithCustomToken(customToken);
      const token = await getIdToken();
      this.handleLoginProcess(token, 'code');
    } catch (error) {
      this.setState({
        connectError:
          'That code is invalid or has expired. Request a new one.',
        loading: false,
      });
    }
  };

  // --- render ---------------------------------------------------------------

  // The card's logo slot. On a white-label subdomain the login page passes
  // the coach's `brand` ({ logo, name }, logo already vetted as https), and
  // the card carries THEIR logo — or their business name when they haven't
  // uploaded one — instead of Nubod's. Everywhere else: the Nubod logo.
  renderBrandMark() {
    const { brand } = this.props;
    if (brand && brand.logo) {
      return (
        <img
          src={brand.logo}
          alt={brand.name || 'Logo'}
          className='auth-brand-logo auth-brand-logo--tenant'
        />
      );
    }
    if (brand && brand.name) {
      return <span className='auth-brand-name'>{brand.name}</span>;
    }
    return <img src={NubodLogo} alt='Nubod' className='auth-brand-logo' />;
  }

  render() {
    const { connectError, passwordError, passwordReset, loading } = this.state;
    const { isAssessment, isLogin } = this.props;
    const isSignup = !isLogin || isAssessment;
    const isOnSignupPath = window.location.pathname === '/signup';
    const showCode = !isSignup && this.state.codeMode;

    if (passwordReset) {
      return (
        <div className='auth-card'>
          <div className='auth-brand'>{this.renderBrandMark()}</div>
          <PasswordResetForm
            onCancel={() => this.setState({ passwordReset: false })}
          />
        </div>
      );
    }

    const submitLabel = isOnSignupPath
      ? loading
        ? 'Creating account…'
        : 'Create account'
      : loading
        ? 'Signing in…'
        : 'Log in';

    return (
      <div className='auth-card'>
        {!isAssessment && (
          <div className='auth-brand'>{this.renderBrandMark()}</div>
        )}

        <div className='auth-header'>
          <h1 className='auth-title'>
            {isOnSignupPath ? 'Create your account' : 'Welcome'}
          </h1>
          <p className='auth-sub muted'>
            {isOnSignupPath
              ? 'Sign up to track your training, nutrition, and progress.'
              : 'Log in to pick up where you left off.'}
          </p>
        </div>

        {connectError ? (
          <div className='auth-alert auth-alert-error'>{connectError}</div>
        ) : null}

        {!showCode && (
          <form className='auth-form' onSubmit={this.handleSubmit}>
          {isAssessment ? (
            <div className='auth-field'>
              <label htmlFor='auth-name' className='auth-label'>
                First name
              </label>
              <input
                id='auth-name'
                type='text'
                className='auth-input'
                onChange={(e) => this.handleLoginForm(e, 'name')}
                autoComplete='given-name'
              />
            </div>
          ) : null}

          {isOnSignupPath ? (
            <div className='auth-field'>
              <label htmlFor='auth-lastname' className='auth-label'>
                Last name
              </label>
              <input
                id='auth-lastname'
                type='text'
                className='auth-input'
                onChange={(e) => this.handleLoginForm(e, 'lastName')}
                autoComplete='family-name'
              />
            </div>
          ) : null}

          <div className='auth-field'>
            <label htmlFor='auth-email' className='auth-label'>
              Email
            </label>
            <input
              id='auth-email'
              type='email'
              className='auth-input'
              placeholder='you@example.com'
              onChange={(e) => this.handleLoginForm(e, 'email')}
              autoComplete='email'
            />
          </div>

          <div className='auth-field'>
            <div className='auth-label-row'>
              <label htmlFor='auth-password' className='auth-label'>
                Password
              </label>
              {!isSignup && (
                <button
                  type='button'
                  className='auth-link'
                  onClick={() =>
                    this.setState({ passwordReset: true, connectError: null })
                  }
                >
                  Forgot?
                </button>
              )}
            </div>
            <PasswordField
              id='auth-password'
              onChange={(e) => this.handleLoginForm(e, 'password')}
              placeholder={isSignup ? 'At least 6 characters' : 'Password'}
              autoComplete={isSignup ? 'new-password' : 'current-password'}
            />
          </div>

          {passwordError ? (
            <div className='auth-alert auth-alert-error'>{passwordError}</div>
          ) : null}

          <button
            type='submit'
            className='btn btn-grad auth-submit'
            disabled={loading}
          >
            {submitLabel}
          </button>
        </form>
        )}

        {showCode ? (
          <form className='auth-form' onSubmit={this.handleVerifyCode}>
            <div className='auth-field'>
              <label htmlFor='auth-code-email' className='auth-label'>
                Email
              </label>
              <input
                id='auth-code-email'
                type='email'
                className='auth-input'
                placeholder='you@example.com'
                value={this.state.loginObj.email || ''}
                onChange={(e) => this.handleLoginForm(e, 'email')}
                autoComplete='email'
              />
            </div>
            {this.state.codeSent ? (
              <div className='auth-field'>
                <label htmlFor='auth-code' className='auth-label'>
                  6-digit code
                </label>
                <input
                  id='auth-code'
                  type='text'
                  inputMode='numeric'
                  maxLength={6}
                  className='auth-input'
                  placeholder='••••••'
                  value={this.state.code}
                  onChange={(e) =>
                    this.setState({
                      code: e.target.value.replace(/[^0-9]/g, ''),
                    })
                  }
                  autoFocus
                />
              </div>
            ) : null}
            {!this.state.codeSent ? (
              <button
                type='button'
                className='btn btn-grad auth-submit'
                disabled={loading}
                onClick={this.handleSendCode}
              >
                {loading ? 'Sending…' : 'Send code'}
              </button>
            ) : (
              <button
                type='submit'
                className='btn btn-grad auth-submit'
                disabled={loading}
              >
                {loading ? 'Signing in…' : 'Sign in'}
              </button>
            )}
          </form>
        ) : null}

        {!isSignup && !isOnSignupPath ? (
          <button
            type='button'
            className='auth-link'
            style={{ display: 'block', margin: '14px auto 0' }}
            onClick={() =>
              this.setState({
                codeMode: !this.state.codeMode,
                codeSent: false,
                code: '',
                connectError: null,
                passwordError: null,
              })
            }
          >
            {this.state.codeMode
              ? 'Sign in with a password instead'
              : 'Sign in with a code instead'}
          </button>
        ) : null}

        {isAssessment ? (
          <p className='auth-policy muted'>
            By {isOnSignupPath ? 'creating an account' : 'logging in'}, you
            agree to our <Link to='/disclaimer'>Disclaimer</Link> and{' '}
            <a href='/privacy' target='_blank' rel='noopener noreferrer'>
              Privacy Policy
            </a>
            , and to receive emails about your account and coaching.
          </p>
        ) : null}
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  assessmentResults: state.assessment.assessmentResults,
  assessmentProfile: state.assessment.profile,
  isMailSent: state.auth.isMailSent,
});

// withRouter (outermost — must wrap connect, not the reverse, or connect's
// shouldComponentUpdate blocks router prop updates) guarantees this.props.history
// is always present from the Router context. Previously history was only passed
// by some parents, so the registration path rendered with history=undefined and
// crashed on history.push ("undefined is not an object (evaluating 'a.push')").
export default withRouter(connect(mapStateToProps)(AuthModal));
